import { ITEM_TYPES } from '@wix/advanced-seo-utils';
import { membersAreaMenuItemsClick } from '@wix/bi-logger-members-app-uou/v2';
import {
  getPageIdAndAppDefIdFromWidgetId,
  WidgetId,
} from '@wix/members-area-app-definitions';

import { Experiment } from '../../../constants';
import type {
  ContextProps,
  ContextServices,
  ErrorStateService,
  FlowAPI,
  MultiStateBoxService,
  RouteData,
  State,
  WixCodeApi,
} from '../../../types';
import { getIsTabAccessibleWithUserRoles } from '../utils';
import { emitTabChangedEventToPlugins } from './widget-plugins';

type NavigationRulesProps = Pick<ContextServices, 'membersService'> &
  Pick<ContextProps, 'state'> & {
    routeData: RouteData;
    isEditor: boolean;
  };

export const redirectToLogin = async (
  multiStateBoxService: MultiStateBoxService,
  wixCodeApi: WixCodeApi,
  errorStateService: ErrorStateService,
) => {
  await multiStateBoxService.showErrorAppState();
  errorStateService.resetToNotFoundContent();
  // Does not work in SSR, renders login in CSR
  // Do not add await, it will stop execution of the rest of the code
  wixCodeApi.user.promptLogin({});
};

export const redirectToErrorPage = (
  contextProps: ContextProps,
  contextServices: ContextServices,
  error: unknown,
) => {
  const { flowAPI } = contextProps;
  const { multiStateBoxService, errorStateService } = contextServices;

  const shouldNavigate = flowAPI.experiments.enabled(
    Experiment.EnablePageLoadErrorState,
  );

  if (!shouldNavigate) {
    throw error;
  }

  multiStateBoxService.showErrorAppState();
  errorStateService.setToPageLoadErrorContent(error);
};

export const redirectTo404 = (
  multiStateBoxService: MultiStateBoxService,
  errorStateService: ErrorStateService,
) => {
  multiStateBoxService.showErrorAppState();

  errorStateService.resetToNotFoundContent();
};

const getNavigationRules = ({
  state,
  routeData,
  membersService,
  isEditor,
}: NavigationRulesProps) => {
  const { stateId, slugOrId, isStatePrivate, visibleForRoles } = routeData;
  const { currentMember, viewedMember } = state.members;

  const isViewedMemberCurrentMember =
    membersService.isViewedMemberCurrentMember(slugOrId, currentMember);
  const isTabInaccessible = !getIsTabAccessibleWithUserRoles(
    state.viewedMemberRoles,
    visibleForRoles,
  );

  const isRouteDataMissing = !stateId;
  const isRouteDataOrViewedMemberMissing = isRouteDataMissing || !viewedMember;
  const isPrivateAndNotViewed = isStatePrivate && !isViewedMemberCurrentMember;
  const shouldRedirectPublicTabToLogin =
    !currentMember && !isStatePrivate && !slugOrId;
  const shouldRedirectPrivateTabToLogin =
    !isEditor && !isRouteDataMissing && isStatePrivate && !currentMember;

  const shouldRedirectToLogin =
    shouldRedirectPrivateTabToLogin || shouldRedirectPublicTabToLogin;
  const shouldRedirectTo404 =
    !isEditor &&
    (isRouteDataOrViewedMemberMissing ||
      isTabInaccessible ||
      isPrivateAndNotViewed);

  return { shouldRedirectToLogin, shouldRedirectTo404 };
};

const emitMembersAreaMenuItemClickBIEvent = (
  flowAPI: FlowAPI,
  routeData: RouteData,
) => {
  const { pageId, appDefId } = getPageIdAndAppDefIdFromWidgetId(
    routeData.visibleWidgetId,
  );
  flowAPI.bi?.report(
    membersAreaMenuItemsClick({
      pageName: pageId,
      originAppId: appDefId,
    }),
  );
};

const showTab = async (
  {
    flowAPI,
    wixCodeApi,
    state,
  }: { flowAPI: FlowAPI; wixCodeApi: WixCodeApi; state: State },
  {
    multiStateBoxService,
    routeDataService,
    widgetPluginHostService,
  }: Pick<
    ContextServices,
    'multiStateBoxService' | 'routeDataService' | 'widgetPluginHostService'
  >,
) => {
  const routeData = routeDataService.getRouteData();

  // During installation, widget data is not yet available
  if (!routeData.stateId) {
    return;
  }

  await widgetPluginHostService.membersAreaWidgetReady();
  await multiStateBoxService.showSlotStateById(routeData.stateId);

  await emitTabChangedEventToPlugins(state, {
    widgetPluginHostService,
    routeDataService,
  });

  if (
    !routeData.isStatePrivate &&
    routeData.visibleWidgetId !== WidgetId.About
  ) {
    wixCodeApi.seo.renderSEOTags({
      itemType: ITEM_TYPES.MEMBERS_AREA_PROFILE_TABS,
      itemData: {},
    });
  }

  emitMembersAreaMenuItemClickBIEvent(flowAPI, routeData);
};

export const handleNavigation = (
  {
    wixCodeApi,
    state,
    flowAPI,
  }: Pick<ContextProps, 'wixCodeApi' | 'state' | 'flowAPI'>,
  {
    multiStateBoxService,
    errorStateService,
    routeDataService,
    membersService,
    widgetPluginHostService,
    menuService,
  }: Pick<
    ContextServices,
    | 'membersService'
    | 'multiStateBoxService'
    | 'errorStateService'
    | 'routeDataService'
    | 'widgetPluginHostService'
    | 'menuService'
  >,
) => {
  const routeData = routeDataService.getRouteData();
  const { shouldRedirectToLogin, shouldRedirectTo404 } = getNavigationRules({
    state,
    membersService,
    routeData,
    isEditor: flowAPI.environment.isEditor,
  });

  const shouldSet404SeoStatusCode =
    flowAPI.experiments.enabled(Experiment.Enable404SeoStatusCode) &&
    shouldRedirectTo404;

  if (shouldSet404SeoStatusCode) {
    wixCodeApi.seo.setSeoStatusCode(404);
  }

  if (shouldRedirectToLogin) {
    return redirectToLogin(multiStateBoxService, wixCodeApi, errorStateService);
  }

  if (shouldRedirectTo404) {
    return redirectTo404(multiStateBoxService, errorStateService);
  }

  menuService.selectMenuItem(routeData.path);

  return showTab(
    { flowAPI, wixCodeApi, state },
    {
      widgetPluginHostService,
      routeDataService,
      multiStateBoxService,
    },
  );
};
